import {
  Fragment,
  ReactNode,
  createElement,
  useRef,
  useState,
  useEffect,
} from 'react';

import { Check, Copy } from 'lucide-react';

import { type TextSize, getIconPixels, textSizes } from '@/theme/size-tokens';

export type TextProps = {
  /** Content to be rendered within the text element */
  children: ReactNode;

  /**
   * Semantic HTML element to render
   * @default 'p'
   */
  as?:
    | 'p'
    | 'span'
    | 'label'
    | 'strong'
    | 'em'
    | 'small'
    | 'mark'
    | 'del'
    | 'ins'
    | 'sub'
    | 'sup'
    | 'abbr'
    | 'cite'
    | 'q';

  /**
   * Color variant
   * @default 'body'
   */
  variant?:
    | 'body'
    | 'muted'
    | 'small'
    | 'primary'
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'neutral'
    | 'teal'
    | 'inherit'
    | 'current';

  /**
   * Text size - uses unified size system
   * @default based on variant
   */
  size?: TextSize;

  /**
   * Font weight
   * @default 'semibold'
   */
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold';

  /**
   * Text alignment
   * @default 'left'
   */
  align?: 'left' | 'center' | 'right' | 'justify';

  /**
   * Line height / leading
   * @default 'normal'
   */
  leading?: 'none' | 'tight' | 'snug' | 'normal' | 'relaxed' | 'loose';

  /**
   * Letter spacing / tracking
   * @default 'normal'
   */
  tracking?: 'tighter' | 'tight' | 'normal' | 'wide' | 'wider' | 'widest';

  /**
   * Text transform
   */
  transform?: 'uppercase' | 'lowercase' | 'capitalize' | 'normal';

  /**
   * Text decoration
   */
  decoration?: 'underline' | 'line-through' | 'none';

  /**
   * Font style
   */
  italic?: boolean;

  /**
   * Truncate text with ellipsis
   */
  truncate?: boolean;

  /**
   * Limit text to specified number of lines (uses line-clamp)
   */
  lineClamp?: 1 | 2 | 3 | 4 | 5 | 6;

  /**
   * Text wrapping behavior
   */
  wrap?: 'wrap' | 'nowrap' | 'balance' | 'pretty';

  /**
   * Word break behavior
   */
  wordBreak?: 'normal' | 'words' | 'all' | 'keep';

  /**
   * Text opacity (0-100)
   */
  opacity?: 0 | 25 | 50 | 75 | 100;

  /**
   * Add bottom margin
   * @default false
   */
  hasMargin?: boolean;

  /**
   * Make text selectable or not
   */
  selectable?: boolean;

  /**
   * Show copy-to-clipboard functionality
   * @default false
   */
  copy?: boolean;

  /**
   * Copy mode: 'icon' shows a copy button, 'text' makes the text itself clickable
   * @default 'icon'
   */
  copyMode?: 'icon' | 'text';

  /**
   * Additional CSS classes (use as last resort)
   */
  className?: string;

  /**
   * ID for the element
   */
  id?: string;

  /**
   * HTML for attribute (useful when as="label")
   */
  htmlFor?: string;

  /**
   * Appends a visual required asterisk (*) to the text.
   * The asterisk is aria-hidden so assistive tech relies on the associated
   * input's required/aria-required attribute instead.
   */
  required?: boolean;
};

/**
 * Text - Versatile typography component for body text
 * Handles all common text styling needs through props
 */
function Text({
  children,
  as = 'p',
  variant = 'body',
  size,
  weight = 'normal',
  align,
  leading,
  tracking,
  transform,
  decoration,
  italic = false,
  truncate = false,
  lineClamp,
  wrap,
  wordBreak,
  opacity,
  hasMargin = false,
  selectable,
  copy = false,
  copyMode = 'icon',
  className = '',
  id,
  htmlFor,
  required = false,
}: Readonly<TextProps>) {
  const textRef = useRef<HTMLElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!textRef.current) return;

    // Guard against environments without the Clipboard API (SSR, unsupported browsers, tests)
    if (
      typeof navigator === 'undefined' ||
      !navigator.clipboard ||
      typeof navigator.clipboard.writeText !== 'function'
    ) {
      console.error('Clipboard API is not available in this environment.');
      return;
    }
    try {
      const textContent = textRef.current.textContent || '';
      await navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  useEffect(() => {
    if (copied && copyMode === 'text') {
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copied, copyMode]);
  const variantClasses = {
    body: 'text-foreground',
    muted: 'text-muted-foreground',
    small: 'text-muted-foreground',
    primary: 'text-primary',
    success: 'text-success',
    warning: 'text-warning',
    error: 'text-error',
    info: 'text-info',
    neutral: 'text-neutral',
    teal: 'text-teal',
    inherit: 'text-inherit',
    current: 'text-current',
  };

  const sizeClasses = textSizes;

  const weightClasses = {
    light: 'font-light',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
    extrabold: 'font-extrabold',
  };

  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
    justify: 'text-justify',
  };

  const leadingClasses = {
    none: 'leading-none',
    tight: 'leading-tight',
    snug: 'leading-snug',
    normal: 'leading-normal',
    relaxed: 'leading-relaxed',
    loose: 'leading-loose',
  };

  const trackingClasses = {
    tighter: 'tracking-tighter',
    tight: 'tracking-tight',
    normal: 'tracking-normal',
    wide: 'tracking-wide',
    wider: 'tracking-wider',
    widest: 'tracking-widest',
  };

  const transformClasses = {
    uppercase: 'uppercase',
    lowercase: 'lowercase',
    capitalize: 'capitalize',
    normal: 'normal-case',
  };

  const decorationClasses = {
    underline: 'underline',
    'line-through': 'line-through',
    none: 'no-underline',
  };

  const wrapClasses = {
    wrap: 'text-wrap',
    nowrap: 'text-nowrap',
    balance: 'text-balance',
    pretty: 'text-pretty',
  };

  const wordBreakClasses = {
    normal: 'break-normal',
    words: 'break-words',
    all: 'break-all',
    keep: 'break-keep',
  };

  const opacityClasses = {
    0: 'opacity-0',
    25: 'opacity-25',
    50: 'opacity-50',
    75: 'opacity-75',
    100: 'opacity-100',
  };

  const lineClampClasses = {
    1: 'line-clamp-1',
    2: 'line-clamp-2',
    3: 'line-clamp-3',
    4: 'line-clamp-4',
    5: 'line-clamp-5',
    6: 'line-clamp-6',
  };

  const defaultSizes = {
    body: 'lg',
    muted: 'base',
    small: 'sm',
    primary: 'base',
    success: 'base',
    warning: 'base',
    error: 'base',
    info: 'base',
    neutral: 'base',
    teal: 'base',
    inherit: 'base',
    current: 'base',
  } as const;

  const finalSize = size ?? defaultSizes[variant];
  const margin = hasMargin ? 'mb-3 sm:mb-4' : '';

  const classes = [
    variantClasses[variant],
    sizeClasses[finalSize],
    weightClasses[weight],
    margin,
    align && alignClasses[align],
    leading && leadingClasses[leading],
    tracking && trackingClasses[tracking],
    transform && transformClasses[transform],
    decoration && decorationClasses[decoration],
    italic && 'italic',
    truncate && 'truncate',
    lineClamp && lineClampClasses[lineClamp],
    wrap && wrapClasses[wrap],
    wordBreak && wordBreakClasses[wordBreak],
    opacity !== undefined && opacityClasses[opacity],
    selectable === true && 'select-all',
    selectable === false && 'select-none',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const elementProps: Record<string, unknown> = {
    'data-slot': 'text',
    className: classes,
    ...(id && { id }),
    ...(htmlFor && as === 'label' && { htmlFor }),
    ref: textRef,
    ...(copy &&
      copyMode === 'text' && {
        role: 'button',
        tabIndex: 0,
        onClick: handleCopy,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCopy();
          }
        },
        'aria-label': `Copy ${children} to clipboard`,
        'aria-pressed': copied,
        className:
          `${classes} ${copy && copyMode === 'text' ? 'cursor-pointer' : ''}`.trim(),
      }),
  };

  const finalChildren = required
    ? createElement(
        Fragment,
        null,
        children,
        createElement(
          'span',
          { className: 'text-error ml-1', 'aria-hidden': 'true' },
          '*'
        )
      )
    : children;

  const textElement = createElement(as, elementProps, finalChildren);

  if (copy && copyMode === 'text') {
    return (
      <div className="group relative block w-full min-w-0">
        {textElement}
        <span
          className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 transform rounded-md bg-neutral-800 px-2 py-1 text-xs whitespace-nowrap text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          role="tooltip"
          aria-hidden="true"
        >
          {copied ? 'Copied!' : 'Click to copy'}
          <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-800" />
        </span>
      </div>
    );
  }

  if (copy) {
    return (
      <span className="group inline-flex items-center gap-2">
        {textElement}
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex cursor-pointer items-center justify-center opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
          aria-label="Copy to clipboard"
        >
          {copied ? (
            <Check
              size={getIconPixels('xs', 'desktop')}
              className="text-success transition-all"
            />
          ) : (
            <Copy
              size={getIconPixels('xs', 'desktop')}
              className="text-neutral transition-all"
            />
          )}
        </button>
      </span>
    );
  }

  return textElement;
}

Text.displayName = 'Text';

export default Text;
